import type { ApiResponse } from '../../types'
import type {
  CircleSummaryDto,
  CircleDetailDto,
  CreateCircleRequest,
  RosterParams,
  RosterResponse,
  InvitationPreviewDto,
  InvitationCreatedDto,
  StandingDto,
} from '../../types/community'
import { client, unwrap, apiError, CotApiError } from './client'

// COTC_Circles owns the bodies. All read failures surface as CotApiError (errors[0] ?? message).
// `listCircles` feeds the Foundation sidebar Circles section.

async function call<T>(run: () => Promise<ApiResponse<T>>): Promise<T> {
  try {
    return unwrap(await run())
  } catch (err) {
    if (err instanceof CotApiError) throw err
    throw apiError(err)
  }
}

/** GET /circles — sidebar summaries for the caller's circles. */
export async function listCircles(): Promise<CircleSummaryDto[]> {
  return call(async () => (await client.get<ApiResponse<CircleSummaryDto[]>>('/circles')).data)
}

/** GET /circles/{id} — type-dependent detail (roster stats or family table). */
export async function getCircle(id: string): Promise<CircleDetailDto> {
  return call(async () => (await client.get<ApiResponse<CircleDetailDto>>(`/circles/${id}`)).data)
}

/** POST /circles — create a circle (type immutable forever). */
export async function createCircle(req: CreateCircleRequest): Promise<CircleSummaryDto> {
  return call(async () => (await client.post<ApiResponse<CircleSummaryDto>>('/circles', req)).data)
}

/** PATCH /circles/{id} — rename (owner). */
export async function renameCircle(id: string, name: string): Promise<CircleSummaryDto> {
  return call(async () => (await client.patch<ApiResponse<CircleSummaryDto>>(`/circles/${id}`, { name })).data)
}

/** POST /circles/{id}/leave. */
export async function leaveCircle(id: string): Promise<void> {
  await call(async () => (await client.post<ApiResponse>(`/circles/${id}/leave`)).data)
}

/** GET /circles/{id}/roster — gaming circle, member-only. */
export async function getRoster(id: string, params: RosterParams = {}): Promise<RosterResponse> {
  return call(async () =>
    (await client.get<ApiResponse<RosterResponse>>(`/circles/${id}/roster`, { params })).data,
  )
}

/** POST /circles/{id}/invitations — mint an invite link. */
export async function createInvitation(id: string): Promise<InvitationCreatedDto> {
  return call(async () => (await client.post<ApiResponse<InvitationCreatedDto>>(`/circles/${id}/invitations`)).data)
}

/** GET /invitations/{token} — invite preview. */
export async function getInvitationPreview(token: string): Promise<InvitationPreviewDto> {
  return call(async () => (await client.get<ApiResponse<InvitationPreviewDto>>(`/invitations/${token}`)).data)
}

/** POST /invitations/{token}/accept. */
export async function acceptInvitation(token: string): Promise<void> {
  await call(async () => (await client.post<ApiResponse>(`/invitations/${token}/accept`)).data)
}

/** POST /circles/{id}/join-requests. */
export async function requestToJoin(id: string, message?: string): Promise<void> {
  await call(async () => (await client.post<ApiResponse>(`/circles/${id}/join-requests`, { message })).data)
}

/** GET /standing — the trust-ladder card. */
export async function getStanding(): Promise<StandingDto> {
  return call(async () => (await client.get<ApiResponse<StandingDto>>('/standing')).data)
}

/** POST /families/{familyId}/vouches. */
export async function vouchForFamily(familyId: string): Promise<void> {
  await call(async () => (await client.post<ApiResponse>(`/families/${familyId}/vouches`)).data)
}
